import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './EmailVerify.module.css';

const ForgotPassword = () => {
  const { t } = useTranslation('login');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setStatus('error');
      setMessage(t('login.emailRequired', 'Email is required'));
      return;
    }

    setStatus('loading');

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    console.log('🔍 ForgotPassword - API URL:', apiUrl);
    console.log('🔍 ForgotPassword - Sending request to:', `${apiUrl}/auth/forgot-password`);
    console.log('🔍 ForgotPassword - Email:', email);

    try {
      const response = await fetch(
        `${apiUrl}/auth/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      console.log('🔍 ForgotPassword - Response status:', response.status);
      const data = await response.json();
      console.log('🔍 ForgotPassword - Response data:', data);

      if (response.ok) {
        setStatus('success');
        setMessage(t('login.resetLinkSent', 'If an account exists with that email, we\'ve sent a password reset link.'));
      } else {
        setStatus('error');
        setMessage(data.message || t('login.resetFailed', 'Failed to send reset email'));
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setStatus('error');
      setMessage(t('login.resetFailed', 'Failed to send reset email'));
    }
  };

  return (
    <div className={styles.verifyContainer}>
      <div className={styles.verifyCard}>
        {/* Icon */}
        <div className={`${styles.iconContainer} ${status === 'success' ? styles.success : status === 'error' ? styles.error : ''}`}>
          {status === 'loading' && (
            <div className={styles.spinner}></div>
          )}
          {status === 'success' && (
            <span className={`${styles.icon} ${styles.checkmark}`}>✓</span>
          )}
          {status === 'error' && (
            <span className={styles.icon}>✕</span>
          )}
          {status === 'idle' && (
            <span className={styles.icon}>🔒</span>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          <h1 className={styles.title}>
            {status === 'success' 
              ? t('login.checkYourEmail', 'Check Your Email')
              : t('login.resetPassword', 'Reset Password')
            }
          </h1>

          {status === 'success' ? (
            <>
              <p className={styles.message}>{message}</p>
              <div className={styles.buttonGroup}>
                <Link to="/login-page" className={`${styles.button} ${styles.primaryButton}`}>
                  {t('login.backToLogin', 'Back to Login')}
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className={styles.message}>
                {t('login.enterEmailForReset', 'Enter your email address and we\'ll send you a link to reset your password.')}
              </p>

              <form onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                  <label htmlFor="email" className={styles.inputLabel}>
                    {t('login.email', 'Email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.inputField}
                    placeholder={t('login.enterEmail', 'Enter your email')}
                    disabled={status === 'loading'}
                    required
                  />
                </div>

                {status === 'error' && (
                  <div className={styles.errorText}>
                    ⚠️ {message}
                  </div>
                )}

                <div className={styles.buttonGroup}>
                  <Link 
                    to="/login-page" 
                    className={`${styles.button} ${styles.secondaryButton}`}
                  >
                    {t('login.cancel', 'Cancel')}
                  </Link>
                  <button
                    type="submit"
                    className={`${styles.button} ${styles.primaryButton}`}
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' 
                      ? t('login.sending', 'Sending...')
                      : t('login.sendResetLink', 'Send Reset Link')
                    }
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
